import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { NewsDoc, NewsItem } from "../types";

// ── Pickleball-uutiset ────────────────────────────────────────
//
// Otsikot tulevat news/{sourceId}-dokumentista, jonka ajastettu funktio
// fetchPickleballNews täyttää kerran tunnissa. Sovellus ei hae syötettä
// itse: lähteen sivu ei salli selainkutsuja, eikä jokaisen pelaajan
// tarvitse hakea samaa listaa. Napautus avaa jutun lähteen sivulle.

const C = {
  card: "var(--sl-card)",
  navy: "var(--sl-primary)", text: "var(--sl-text)", muted: "var(--sl-muted)", white: "#ffffff", border: "var(--sl-border)",
};
const F = { heading: "'Rajdhani', sans-serif", body: "'Sora', sans-serif" };
const card: React.CSSProperties = {
  background: C.card, border: `1px solid ${C.border}`,
  borderRadius: 16, padding: 0, marginBottom: 14, overflow: "hidden",
  boxShadow: "0 4px 16px rgba(22,59,104,0.06)",
};

const SOURCE_ID = "thedink";

// ── Lukunäkymä sovelluksen sisällä ───────────────────────────
//
// Standalone-PWA:ssa ulkoinen linkki vei pois sovelluksesta eikä
// takaisin päässyt. Juttu näytetään siksi sovelluksen päällä omassa
// näkymässä, jossa on Takaisin-painike. Laitteen oma paluu toimii
// myös: avaus työntää historiaan tilan, ja popstate sulkee näkymän.
const Reader: React.FC<{ item: NewsItem; source: string; lang: "fi" | "en"; onClose: () => void }> = ({ item, source, lang, onClose }) => {
  useEffect(() => {
    // Vain yksi tila per avaus: kehitystilan tuplasuoritus työntäisi
    // muuten kaksi, ja paluuta pitäisi painaa kahdesti.
    if (!window.history.state?.spinloopReader) {
      window.history.pushState({ spinloopReader: true }, "");
    }
    const onPop = () => onClose();
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [onClose]);

  const back = () => {
    // Paluu historiassa laukaisee popstaten, joka sulkee näkymän.
    if (window.history.state?.spinloopReader) window.history.back();
    else onClose();
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999, background: C.card,
      display: "flex", flexDirection: "column",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 12px", paddingTop: "calc(10px + env(safe-area-inset-top))",
        background: C.navy, color: C.white, flexShrink: 0,
      }}>
        <button onClick={back} style={{
          background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8,
          color: C.white, fontSize: 14, fontWeight: 700, padding: "8px 12px", cursor: "pointer",
          fontFamily: F.body, whiteSpace: "nowrap",
        }}>
          ← {lang === "fi" ? "Takaisin" : "Back"}
        </button>
        <div style={{ flex: 1, minWidth: 0, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", opacity: 0.85 }}>
          {source} · {item.title}
        </div>
        <a href={item.link} target="_blank" rel="noopener noreferrer" style={{
          color: C.white, fontSize: 12, textDecoration: "none", whiteSpace: "nowrap", opacity: 0.85,
        }}>
          {lang === "fi" ? "Selaimessa ↗" : "Browser ↗"}
        </a>
      </div>
      <iframe src={item.link} title={item.title} style={{ flex: 1, border: "none", width: "100%" }} />
    </div>
  );
};

const fmt = (iso: string) =>
  new Date(iso).toLocaleDateString("fi-FI", { day: "numeric", month: "numeric" });

const NewsFeed: React.FC<{ lang?: "fi" | "en" }> = ({ lang = "fi" }) => {
  const [news, setNews] = useState<NewsDoc | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [reading, setReading] = useState<NewsItem | null>(null);
  const closeReader = React.useCallback(() => setReading(null), []);

  useEffect(() => {
    let alive = true;
    getDoc(doc(db, "news", SOURCE_ID)).then(snap => {
      if (!alive) return;
      setNews(snap.exists() ? (snap.data() as NewsDoc) : null);
      setLoaded(true);
    }).catch(err => { console.error("news", err); if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  if (!loaded) return null;
  if (!news || news.items.length === 0) {
    return (
      <div style={{ ...card, padding: 18, textAlign: "center", color: C.muted, fontSize: 13 }}>
        {lang === "fi" ? "Uutisia ei ole vielä haettu." : "News has not been fetched yet."}
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
        📰 {lang === "fi" ? "Pickleball-uutiset" : "Pickleball news"}
        <span style={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, marginLeft: 6 }}>
          · {news.source}{news.language === "en" && lang === "fi" ? " · englanniksi" : ""}
        </span>
      </div>
      <div style={card}>
        {news.items.map((it, i) => (
          <button key={it.link} onClick={() => setReading(it)} style={{
            display: "block", width: "100%", textAlign: "left", background: "none", cursor: "pointer",
            padding: "12px 16px", border: "none",
            borderTop: i === 0 ? "none" : `1px solid ${C.border}`,
          }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text, lineHeight: 1.45, fontFamily: F.body }}>{it.title}</div>
            {it.summary && (
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginTop: 3, fontFamily: F.body }}>{it.summary}</div>
            )}
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4, fontFamily: F.body }}>{fmt(it.publishedAt)} · {news.source} ›</div>
          </button>
        ))}
        {reading && <Reader item={reading} source={news.source} lang={lang} onClose={closeReader} />}
        <div style={{ padding: "8px 16px", fontSize: 10, color: C.muted, borderTop: `1px solid ${C.border}` }}>
          {lang === "fi"
            ? <>Otsikot haetaan lähteen syötteestä kerran tunnissa. Juttu avautuu sovelluksen sisällä, lähde <a href={news.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: C.navy }}>{news.source}</a>.</>
            : <>Headlines come from the source feed once an hour. Articles open inside the app, source <a href={news.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: C.navy }}>{news.source}</a>.</>}
        </div>
      </div>
    </div>
  );
};

export default NewsFeed;
